import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  OutlinedInput,
  Chip,
  FormHelperText,
  Typography,
} from "@mui/material";

const rolesList = ["Admin", "Editor", "Owner"];
const passwordRules = ["At least 8 characters", "One uppercase letter", "One number", "One special character"];
const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const AddUserDialog = ({ open, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({ email: "", displayName: "", phoneNumber: "", password: "", roles: [] });
  const [errors, setErrors] = useState({});
  const [passwordTouched, setPasswordTouched] = useState(false);

  const inputRefs = {
    email: useRef(null),
    displayName: useRef(null),
    phoneNumber: useRef(null),
    roles: useRef(null),
    password: useRef(null),
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.displayName) newErrors.displayName = "Display name is required";
    if (!formData.phoneNumber) newErrors.phoneNumber = "Phone number is required";
    if (!formData.password) newErrors.password = "Password is required";
    else if (!passwordRegex.test(formData.password)) newErrors.password = "Password does not meet requirements";
    if (formData.roles.length === 0) newErrors.roles = "Role is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "password") setPasswordTouched(true);
  };

  const handleRoleChange = (event) => {
    const selectedRoles = event.target.value;
    setFormData((prev) => ({
      ...prev,
      roles: selectedRoles.includes("Owner") ? ["Owner"] : selectedRoles,
    }));
  };

  const handleKeyDown = (event, fieldName) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const fieldNames = Object.keys(inputRefs);
      const currentIndex = fieldNames.indexOf(fieldName);
      const nextField = fieldNames[currentIndex + 1];
      if (nextField) inputRefs[nextField].current?.focus();
      else handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (validateForm()) onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Add New User</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Email"
            name="email"
            fullWidth
            value={formData.email}
            onChange={handleChange}
            onKeyDown={(e) => handleKeyDown(e, "email")}
            inputRef={inputRefs.email}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            label="Display Name"
            name="displayName"
            fullWidth
            value={formData.displayName}
            onChange={handleChange}
            onKeyDown={(e) => handleKeyDown(e, "displayName")}
            inputRef={inputRefs.displayName}
            error={!!errors.displayName}
            helperText={errors.displayName}
          />
          <TextField
            label="Phone Number"
            name="phoneNumber"
            fullWidth
            value={formData.phoneNumber}
            onChange={handleChange}
            onKeyDown={(e) => handleKeyDown(e, "phoneNumber")}
            inputRef={inputRefs.phoneNumber}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber}
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            fullWidth
            value={formData.password}
            onChange={handleChange}
            onKeyDown={(e) => handleKeyDown(e, "password")}
            inputRef={inputRefs.password}
            error={!!errors.password}
            helperText={errors.password }
          />
          <FormControl fullWidth error={!!errors.roles}>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              value={formData.roles}
              onChange={handleRoleChange}
              onKeyDown={(e) => handleKeyDown(e, "roles")}
              inputRef={inputRefs.roles}
              input={<OutlinedInput label="Roles" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((role) => (
                    <Chip key={role} label={role} />
                  ))}
                </Box>
              )}
            >
              {rolesList.map((role) => (
                <MenuItem
                  key={role}
                  value={role}
                  disabled={formData.roles.includes("Owner") && role !== "Owner"}
                >
                  {role}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.roles}</FormHelperText>
          </FormControl>

          <Box mt={1}>
            <Typography variant="body2" sx={{ color: "#8e99a3" }}>
              Password must contain:
            </Typography>
            {passwordRules.map((rule, index) => {
              const isValid = passwordRegex.test(formData.password);
              return (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ color: passwordTouched ? (isValid ? "#43a047" : "#e53935") : "#8e99a3" }}
                >
                  • {rule}
                </Typography>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
